import { useQuery } from "@tanstack/react-query";
import { legacyAdminGet } from "../utils/PrecargaApi";
import { useAuth } from "./useAuth";

/**
 * Custom hook for managing Precarga Card Links
 * @param {object} options - Query options
 * @param {number} options.eventId - Precarga event ID
 * @param {number} options.page - Page number (default 1)
 * @param {number} options.pageSize - Page size (default 20)
 * @param {string} options.status - Filter by status: PENDING, DONE, FAILED
 * @param {string} options.search - Search by user email or phone
 * @param {string} options.sortBy - Sort by: created_at, status
 * @param {string} options.sortDir - Sort direction: asc, desc
 */
export function usePrecargaCardLinks({
  eventId,
  page = 1,
  pageSize = 20,
  status = "",
  search = "",
  sortBy = "created_at",
  sortDir = "desc",
} = {}) {
  const { user } = useAuth();
  const token = user?.token;

  // List card links
  const listQuery = useQuery({
    queryKey: ["precargaCardLinks", { eventId, page, pageSize, status, search, sortBy, sortDir }],
    queryFn: async () => {
      const params = { page, pageSize, sortBy, sortDir };
      if (status) params.status = status;
      if (search) params.search = search;

      return await legacyAdminGet(`${eventId}/card-links`, params);
    },
    enabled: !!token && !!eventId,
    keepPreviousData: true,
  });

  // Get card link detail
  const getCardLinkDetail = async (linkId) => {
    if (!eventId || !linkId) return null;
    return await legacyAdminGet(`${eventId}/card-links/${linkId}/detail`);
  };

  return {
    cardLinks: listQuery.data?.items || [],
    totalCount: listQuery.data?.totalCount || 0,
    hasMore: listQuery.data?.hasMore || false,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    error: listQuery.error,
    refetch: listQuery.refetch,
    getCardLinkDetail,
  };
}

export default usePrecargaCardLinks;
